import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import {
  Plus, Trash2, Save, RefreshCw, Radio, Eye, EyeOff, X, Edit3,
  CheckCircle2, AlertTriangle, Wifi, Activity, Copy
} from 'lucide-react';

const NAS_TYPES = ['other', 'cisco', 'mikrotik', 'huawei', 'juniper', 'pfsense'];

export default function Radius() {
  const [tab, setTab] = useState('clients');
  const [me, setMe]   = useState(null);
  useEffect(() => { api.get('/me').then(r => setMe(r.data.user)).catch(()=>{}); }, []);

  return (
    <div className="stack-lg">
      <header className="row-between">
        <div>
          <h1 className="h1">FreeRADIUS</h1>
          <p className="dim" style={{ fontSize: 14, marginTop: 4 }}>
            Autenticação PPPoE de backup — autoriza qualquer assinante quando o RADIUS principal estiver offline.
          </p>
        </div>
      </header>

      <BackupModeBanner/>

      <div className="tab-bar">
        <div className={'tab ' + (tab === 'clients' ? 'active' : '')} onClick={() => setTab('clients')}>
          <Wifi size={14} style={{ marginRight: 6 }}/> NAS Clients
        </div>
        <div className={'tab ' + (tab === 'logs' ? 'active' : '')} onClick={() => setTab('logs')}>
          <Activity size={14} style={{ marginRight: 6 }}/> Logs de Conexão
        </div>
      </div>

      {tab === 'clients' && <Clients me={me}/>}
      {tab === 'logs'    && <LiveLogs/>}
    </div>
  );
}

function BackupModeBanner() {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get('/radius/status').then(r => setStatus(r.data)).catch(()=>{}); }, []);
  if (!status) return null;
  return (
    <div className="card" style={{
      borderColor: 'rgba(0,212,255,0.3)',
      background: 'linear-gradient(135deg, rgba(0,212,255,0.06), transparent 60%)'
    }}>
      <div className="hrow" style={{ padding: '14px 18px', gap: 14 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--accent-dim)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Radio size={20} color="var(--accent)"/>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            Modo <span style={{color:'var(--accent)'}}>BACKUP</span> · {status.enabled_clients} NAS clients configurados
          </div>
          <div className="dim" style={{ fontSize: 12.5, marginTop: 2 }}>
            Escutando <code>{status.bind_ip}:{status.ports.auth}</code> · accounting em <code>{status.ports.accounting}</code>.
            Fallback <code>0.0.0.0/0</code> ativo: qualquer NAS com a secret correta autentica.
          </div>
        </div>
        <span className={'badge ' + (status.container_state === 'running' ? 'ok' : 'err')}>
          <span className={'dot ' + (status.container_state === 'running' ? 'ok' : 'err')}/>
          {status.container_state === 'running' ? 'Ativo' : status.container_state}
        </span>
      </div>
    </div>
  );
}

// ─── NAS Clients ─────────────────────────────────────────────────
function Clients({ me }) {
  const [clients, setClients] = useState([]);
  const [show, setShow]   = useState(false);
  const [edit, setEdit]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState(null);

  const isAdmin = me?.role === 'admin' || me?.canAll;

  const load = () => api.get('/radius/clients').then(r => setClients(r.data));
  useEffect(() => { load(); }, []);

  const toast = (kind, text) => { setMsg({kind, text}); setTimeout(()=>setMsg(null), 5000); };

  const toggle = async (c) => {
    setBusy(true);
    try {
      await api.put(`/radius/clients/${c.id}`, { enabled: !c.enabled });
      load();
    } catch (e) { toast('err', e.response?.data?.error || 'Erro'); }
    finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Remover NAS "${c.name}" (${c.ip})?`)) return;
    setBusy(true);
    try {
      await api.delete(`/radius/clients/${c.id}`);
      load();
      toast('ok', '✓ NAS removido');
    } catch (e) { toast('err', e.response?.data?.error || 'Erro'); }
    finally { setBusy(false); }
  };

  const reapply = async () => {
    setBusy(true);
    try {
      const r = await api.post('/radius/reapply');
      toast('ok', '✓ ' + (r.data.output || 'Aplicado').trim().split('\n').pop());
    } catch (e) { toast('err', e.response?.data?.error || 'Erro'); }
    finally { setBusy(false); }
  };

  return (
    <div className="stack">
      {msg && (
        <div className={msg.kind === 'ok' ? 'ok-msg' : 'err-msg'} style={{margin:0}}>{msg.text}</div>
      )}

      <div className="card">
        <div className="card-header">
          <Wifi size={15} color="var(--accent)"/>
          <h3 className="h3">NAS Clients</h3>
          <span className="badge muted">{clients.length} total · {clients.filter(c=>c.enabled).length} ativos</span>
          <div className="card-actions">
            {isAdmin && (
              <>
                <button className="btn btn-secondary btn-sm" onClick={reapply} disabled={busy}>
                  <RefreshCw size={12}/> Reaplicar
                </button>
                <button className="btn btn-primary btn-sm" onClick={()=>{ setEdit(null); setShow(true); }}>
                  <Plus size={13}/> Adicionar NAS
                </button>
              </>
            )}
          </div>
        </div>

        {clients.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Wifi size={26}/></div>
            <div className="empty-title">Nenhum NAS específico</div>
            <div className="empty-desc">
              O fallback <code>0.0.0.0/0</code> ainda aceita qualquer NAS com a secret padrão.
              Adicione clients específicos pra usar secrets dedicadas por equipamento.
            </div>
          </div>
        ) : (
          <table className="table">
            <thead><tr>
              <th style={{width:30}}></th>
              <th>Nome</th>
              <th>IP/CIDR</th>
              <th>Tipo</th>
              <th>Descrição</th>
              <th>Status</th>
              {isAdmin && <th style={{width:200}}></th>}
            </tr></thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.id}>
                  <td><span className={'dot ' + (c.enabled ? 'ok' : 'err')}/></td>
                  <td className="mono" style={{fontWeight:600}}>{c.name}</td>
                  <td className="mono" style={{fontSize:12.5}}>{c.ip}</td>
                  <td><span className="badge muted mono">{c.nas_type}</span></td>
                  <td className="dim" style={{fontSize:12.5}}>{c.description || '–'}</td>
                  <td>
                    <span className={'badge ' + (c.enabled ? 'ok' : 'muted')}>
                      {c.enabled ? 'Ativo' : 'Desabilitado'}
                    </span>
                  </td>
                  {isAdmin && (
                    <td style={{textAlign:'right'}}>
                      <SecretButton client={c}/>
                      <button className="btn btn-ghost btn-sm" onClick={()=>toggle(c)} disabled={busy} title={c.enabled?'Desabilitar':'Habilitar'}>
                        {c.enabled ? <X size={12}/> : <CheckCircle2 size={12}/>}
                      </button>
                      <button className="btn btn-ghost btn-icon" onClick={()=>{ setEdit(c); setShow(true); }} title="Editar">
                        <Edit3 size={12}/>
                      </button>
                      <button className="btn btn-ghost btn-icon" onClick={()=>del(c)} title="Remover">
                        <Trash2 size={12}/>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {show && isAdmin && (
        <NasModal client={edit} onClose={()=>{ setShow(false); setEdit(null); load(); }}/>
      )}
    </div>
  );
}

function SecretButton({ client }) {
  const [secret, setSecret] = useState(null);
  const [shown, setShown] = useState(false);

  const toggle = async () => {
    if (shown) { setShown(false); return; }
    if (!secret) {
      const r = await api.get(`/radius/clients/${client.id}/secret`);
      setSecret(r.data.secret);
    }
    setShown(true);
  };

  const copy = () => {
    if (secret) navigator.clipboard.writeText(secret);
  };

  return (
    <span style={{display:'inline-flex', gap:4, alignItems:'center'}}>
      <button className="btn btn-ghost btn-sm" onClick={toggle} title={shown?'Esconder':'Mostrar secret'}>
        {shown ? <EyeOff size={12}/> : <Eye size={12}/>}
      </button>
      {shown && (
        <>
          <code style={{fontSize:11}}>{secret}</code>
          <button className="btn btn-ghost btn-icon" onClick={copy} title="Copiar"><Copy size={11}/></button>
        </>
      )}
    </span>
  );
}

function NasModal({ client, onClose }) {
  const isEdit = !!client;
  const [form, setForm] = useState({
    name: client?.name || '',
    ip: client?.ip || '',
    secret: '',
    nas_type: client?.nas_type || 'other',
    description: client?.description || '',
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const gen = () => {
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    const s = Array.from(arr).map(b => b.toString(36)).join('').slice(0, 24);
    setForm({...form, secret: s});
  };

  const save = async () => {
    setErr(''); setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/radius/clients/${client.id}`, form);
      } else {
        if (!form.secret) { setErr('Secret obrigatória'); setBusy(false); return; }
        await api.post('/radius/clients', form);
      }
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro');
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <h2>{isEdit ? `Editar NAS — ${client.name}` : 'Novo NAS Client'}</h2>

        <div className="grid-2">
          <div className="field">
            <label>Nome (apelido)</label>
            <input className="input" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}
              placeholder="bras-sp01" disabled={isEdit}/>
          </div>
          <div className="field">
            <label>Tipo</label>
            <select className="select" value={form.nas_type} onChange={e=>setForm({...form, nas_type:e.target.value})}>
              {NAS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div className="field">
          <label>IP ou CIDR do NAS</label>
          <input className="input mono" value={form.ip} onChange={e=>setForm({...form, ip:e.target.value})}
            placeholder="203.0.113.50  ou  203.0.113.0/24"/>
        </div>

        <div className="field">
          <label>Shared Secret {isEdit && <span style={{color:'var(--text-mute)'}}>(em branco mantém atual)</span>}</label>
          <div style={{display:'flex', gap:6}}>
            <input className="input mono" value={form.secret} onChange={e=>setForm({...form, secret:e.target.value})}
              placeholder={isEdit ? '••••••• (não alterar)' : 'secret compartilhada com o NAS'}/>
            <button type="button" className="btn btn-secondary btn-sm" onClick={gen}>Gerar</button>
          </div>
        </div>

        <div className="field">
          <label>Descrição (opcional)</label>
          <input className="input" value={form.description} onChange={e=>setForm({...form, description:e.target.value})}
            placeholder="Ex: BRAS principal São Paulo"/>
        </div>

        {err && <div className="err-msg">{err}</div>}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !form.name || !form.ip}>
            <Save size={13}/> {isEdit ? 'Salvar' : 'Adicionar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Logs ─────────────────────────────────────────────────────────
function LiveLogs() {
  const [events, setEvents] = useState([]);
  const [paused, setPaused] = useState(false);
  const seenRef = useRef(new Set());

  const tick = async () => {
    if (paused) return;
    try {
      const r = await api.get('/radius/logs?limit=200');
      const fresh = (r.data.events || []).filter(e => {
        const key = `${e.timestamp || ''}|${e.raw}`;
        if (seenRef.current.has(key)) return false;
        seenRef.current.add(key);
        return true;
      });
      if (fresh.length > 0) {
        setEvents(prev => [...fresh, ...prev].slice(0, 300));
        if (seenRef.current.size > 600) {
          seenRef.current = new Set(Array.from(seenRef.current).slice(-400));
        }
      }
    } catch {}
  };
  useEffect(() => { tick(); const i = setInterval(tick, 3000); return () => clearInterval(i); }, [paused]);

  const stats = events.reduce((acc, e) => {
    acc[e.severity] = (acc[e.severity] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="stack">
      <div className="grid-4">
        <Stat label="Eventos na tela" value={events.length}/>
        <Stat label="Login OK"        value={stats.ok || 0}    kind="ok"/>
        <Stat label="Login falhou"    value={stats.err || 0}   kind="err"/>
        <Stat label="Access-Request"  value={stats.info || 0}  kind="info"/>
      </div>

      <div className="card">
        <div className="card-header">
          <Radio size={15} color={paused ? 'var(--text-mute)' : 'var(--ok)'}/>
          <h3 className="h3">Streaming · logs do FreeRADIUS</h3>
          {!paused && <span className="badge ok"><span className="dot ok pulse"/>LIVE</span>}
          <div className="card-actions">
            <button className="btn btn-secondary btn-sm" onClick={()=>setPaused(p=>!p)}>
              {paused ? 'Retomar' : 'Pausar'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={()=>{setEvents([]); seenRef.current.clear();}}>Limpar</button>
          </div>
        </div>
        <div style={{maxHeight:560, overflow:'auto'}}>
          {events.length === 0 ? (
            <div className="empty-state" style={{padding:48}}>
              <div className="empty-desc">
                Aguardando eventos de autenticação… Quando um cliente PPPoE tentar conectar, os eventos aparecem aqui em tempo real.
              </div>
            </div>
          ) : events.map((e, i) => (
            <div key={i} className="hrow" style={{
              padding:'8px 18px', borderBottom:'1px solid var(--border)',
              fontFamily:'var(--font-mono)', fontSize:11.5, gap:14,
            }}>
              <span style={{color:'var(--text-mute)', minWidth:140, fontSize:10.5}}>
                {e.timestamp || '—'}
              </span>
              <SeverityBadge sev={e.severity}/>
              {e.user && <span style={{color:'var(--accent)', minWidth:120}}>{e.user}</span>}
              {e.nas && <span style={{color:'var(--accent-2)', fontSize:10.5}}>{e.nas}</span>}
              <span style={{flex:1, color:'var(--text-dim)', wordBreak:'break-all', fontSize:11}}>{e.event}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SeverityBadge({ sev }) {
  const m = {
    ok:   ['ok',   'OK'],
    err:  ['err',  'FAIL'],
    warn: ['warn', 'WARN'],
    info: ['info', 'REQ'],
  };
  const [cls, label] = m[sev] || ['muted', '?'];
  return <span className={'badge ' + cls + ' mono'} style={{fontSize:10, minWidth:42, justifyContent:'center'}}>{label}</span>;
}

function Stat({ label, value, kind }) {
  return (
    <div className="stat-card">
      <span className="stat-label">{label}</span>
      <div className="stat-value" style={
        kind === 'ok'   ? {color:'var(--ok)'} :
        kind === 'err'  ? {color:'var(--err)'} :
        kind === 'info' ? {color:'var(--info)'} : {}}>
        {value}
      </div>
    </div>
  );
}
